/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DOMParser } from '@xmldom/xmldom';

import { loadTasks } from 'store/actions/abox/taskActions';
import { formatOptionsForTask } from 'app/utils/formatter/graphql-formatter';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';

import PageTemplate from 'app/components/templates/PageTemplate';
import ContentArea from 'app/components/molecules/PageContent/ContentArea';
import EntitiesList from 'app/containers/Entities/EntitiesList/EntitiesList';
import { tasksDefaultFilters, tasksDefaultOrder, tasksFilterDefinitions } from 'app/components/organisms/TasksView/tasksFilterDefinitions';

import withPermissions from 'app/utils/withPermissions';
import { setDocumentTitle } from 'store/actions/app/appActions';
import { modulesAndPageTitles } from 'app/config/typesConfig';

const VIEW_ID = 'TaskList';

class TaskList extends PureComponent {
    static propTypes = {
        loadTasks: PropTypes.func.isRequired,
        records: PropTypes.array,
        isLoading: PropTypes.bool,
        totalRecords: PropTypes.number,
    };

    static defaultProps = {
        isLoading: false,
        VirtualListProps: {},
        FiltersProps: {},
    };

    state = { feFilters: [] };
    defaultFilters = tasksDefaultFilters;
    defaultOrder = tasksDefaultOrder;

    columnDefinitions = [
        {
            text: 'Task Name',
            field: 'name',
        },
        {
            text: 'Task ID',
            field: 'id',
        },
        {
            text: 'Key',
            field: 'primary.definitionElementId',
            align: 'center',
            width: '130px',
            onClickDisabled: true,
        },
        {
            text: 'Sequence Number',
            field: 'primary.sequenceNumber',
            align: 'center',
            width: '150px',
            onClickDisabled: true,
        },
        {
            text: 'Due Date',
            field: 'primary.dueDate',
            width: '150px',
        },
        {
            text: 'Assignee',
            field: 'assignee.name',
            width: '100px',
        },
        {
            text: 'Priority',
            field: 'primary.priority',
        },
        {
            text: 'Progress',
            field: 'primary.progress',
            align: 'left',
            sortable: false,
        },
        {
            text: 'Status',
            field: 'primary.closedDate',
            renderer: ({ value }) => (!!value ? 'Closed' : 'Open'),
            align: 'center',
            sortable: false,
        },
        {
            text: 'Classes',
            field: 'classes',
            align: 'center',
            onClickDisabled: true,
        },
        {
            text: 'Last modified date',
            field: 'modifiedDate',
        },
        {
            text: 'Modified By',
            field: 'modifiedBy',
        },
        {
            text: 'Created date',
            field: 'createdDate',
        },
        {
            text: 'Created by',
            field: 'createdBy',
        },
        {
            text: 'Process started by',
            field: 'primary.variables.INITIATOR',
            width: '150px',
            onClickDisabled: true,
        },
        {
            text: 'Relations',
            field: 'relations',
            align: 'center',
            onClickDisabled: true,
        },
        {
            text: 'Sharing',
            field: 'sharing',
            width: '120px',
            onClickDisabled: true,
        },
        {
            field: 'Actions',
            align: 'right',
            width: '115px',
            onClickDisabled: true,
        },
    ];

    componentDidMount() {
        this.props.setDocumentTitle(modulesAndPageTitles.abox.tasks);
    }

    @bind
    @memoize()
    buildFilterDefinitions(leftPanelRecords, selectedProcDefId) {
        const nextFilterDefinitions = selectedProcDefId
            ? [...tasksFilterDefinitions].filter(fd => fd.field !== 'primary.definitionElementId')
            : [...tasksFilterDefinitions];

        if (!leftPanelRecords?.length || !selectedProcDefId) return nextFilterDefinitions;

        const selectedDef = leftPanelRecords.find(procDef => procDef.id === selectedProcDefId);

        let keys = [];
        if (selectedDef?.primary?.definition) {
            const doc = new DOMParser().parseFromString(selectedDef.primary.definition, 'xml/text');
            const userTasks = Array.from(doc.getElementsByTagName('userTask'));
            keys = userTasks.map(task => task.getAttribute('id'));
        }

        if (!keys?.length) return nextFilterDefinitions;

        nextFilterDefinitions.splice(2, 0, {
            field: 'primary.definitionElementId',
            type: 'typeahead',
            properties: {
                label: 'KEY',
                name: `pdei${selectedProcDefId}`,
                options: keys.map(key => ({ value: key, label: key })),
            },
            sort: false,
        });
        return nextFilterDefinitions;
    }

    @bind
    loadTasks(options) {
        const formattedOptions = formatOptionsForTask(options.options, this.props.userProfile);
        return this.props.loadTasks({ ...formattedOptions });
    }

    render() {
        const { startIndex, isLoading, records, totalRecords, leftPanelRecords } = this.props;
        const {
            match: {
                params: { id: selectedProcDefId },
            },
        } = this.props;

        return (
            <PageTemplate title="Tasks">
                <ContentArea>
                    <EntitiesList
                        id={VIEW_ID}
                        type="task"
                        headerTitle="Tasks"
                        records={records || []}
                        totalRecords={totalRecords}
                        startIndex={startIndex}
                        isLoading={isLoading}
                        loadData={this.loadTasks}
                        columnDefinitions={this.columnDefinitions}
                        filterDefinitions={this.buildFilterDefinitions(leftPanelRecords, selectedProcDefId)}
                        defaultFilters={this.defaultFilters}
                        disableAdd
                    />
                </ContentArea>
            </PageTemplate>
        );
    }
}

export default connect(
    state => ({
        isLoading: state.abox.task.list.isLoading,
        startIndex: state.abox.task.list.startIndex,
        records: state.abox.task.list.records,
        totalRecords: state.abox.task.list.count,
        userProfile: state.user.profile,
        leftPanelRecords: state.leftPanel.processDefinitions?.data?.records,
    }),
    { loadTasks, setDocumentTitle }
)(withPermissions(TaskList, 'task'));
